import { useCallback, useEffect, useRef, useState } from "react";

import { subscribeToAttendanceEvents } from "../../../lib/realtimeClient";
import { supabase } from "../../../lib/supabase";
import {
  useLazyGetAttendanceBundleQueryQuery,
  useTransferStudentMutation,
} from "../api/attendanceApi";
import { useAuth } from "../../../contexts/AuthContext";
import { isOutsideKBWindow } from "../../absence-tracking/shared/components/Tagesverlauf";
import {
  compareNames,
  getDefaultLateTime,
  calculateLateMinutes,
} from "../utils/attendanceHelpers";
import type {
  AttendanceBundle,
  ConcurrentCourseAttendance,
} from "../../../lib/attendanceBundle";
import { hasGenuineAttendance } from "../../../lib/attendanceBundle";
import type {
  CourseOverlap,
  TempAttendanceMap,
} from "../types/attendanceDialog";

// ============================================================================
// Types
// ============================================================================

export interface UseAttendanceLoaderArgs {
  open: boolean;
  lessonId: string | null | undefined;
  viewMode: "overview" | "edit";
  initialBundle?: AttendanceBundle | null;
  courseOverlaps?: CourseOverlap[];
  /**
   * Override for the school_id used by the loader's secondary fetch
   * paths (`reloadBundle` after a transfer, the realtime resubscribe).
   * On a paired kiosk the React auth context has no signed-in user
   * → `userProfile.school_id` is null → those paths silently no-op.
   * Passing an explicit override keeps them working. The INITIAL
   * load is already covered by `initialBundle` so it doesn't need
   * this override.
   */
  schoolIdOverride?: string | null;
  /**
   * Override for the teacher's user_id (same paths as schoolIdOverride).
   * On a paired kiosk this comes from the chip-login session.
   */
  userIdOverride?: string | null;
}

export interface UseAttendanceLoaderReturn {
  // Core lesson state
  selectedLesson: any | null;
  setSelectedLesson: React.Dispatch<React.SetStateAction<any | null>>;
  attendanceViewMode: "overview" | "edit";
  setAttendanceViewMode: React.Dispatch<
    React.SetStateAction<"overview" | "edit">
  >;
  overviewData: any;
  setOverviewData: React.Dispatch<React.SetStateAction<any>>;
  attendanceLoading: boolean;
  lessonNote: string;
  setLessonNote: React.Dispatch<React.SetStateAction<string>>;
  /** Hausaufgaben for this lesson, from `bundle.diary.homework`. Bound to
   *  the inline Hausaufgaben textarea in EditSection. */
  lessonHomework: string;
  setLessonHomework: React.Dispatch<React.SetStateAction<string>>;
  /** Pristine homework string captured at load time so the save hook can
   *  tell whether the teacher actually edited the field and skip the
   *  diary-upsert RPC otherwise. */
  initialLessonHomework: string;
  tempAttendance: TempAttendanceMap;
  setTempAttendance: React.Dispatch<React.SetStateAction<TempAttendanceMap>>;
  explicitPresentIdsRef: React.MutableRefObject<Set<string>>;
  editedStudentIds: Set<string>;
  setEditedStudentIds: React.Dispatch<React.SetStateAction<Set<string>>>;

  // Protection & auxiliary
  protectionByStudent: Record<
    string,
    {
      protected: boolean;
      reasons: string[];
      hasAnyToday: boolean;
      isAbsentToday: boolean;
    }
  >;
  setProtectionByStudent: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          protected: boolean;
          reasons: string[];
          hasAnyToday: boolean;
          isAbsentToday: boolean;
        }
      >
    >
  >;
  overrideReasons: Record<string, string>;
  setOverrideReasons: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  lessonStatusOverrides: Record<string, { status?: string; note?: string }>;
  prefilledFromPrevious: boolean;
  prefilledAny: boolean;
  recordedMeta: { name: string; atISO: string } | null;
  setRecordedMeta: React.Dispatch<
    React.SetStateAction<{ name: string; atISO: string } | null>
  >;
  diaryMeta: { name: string; atISO: string } | null;
  setDiaryMeta: React.Dispatch<
    React.SetStateAction<{ name: string; atISO: string } | null>
  >;
  canEditExisting: boolean;
  setCanEditExisting: React.Dispatch<React.SetStateAction<boolean>>;
  badgeCounts: { present: number; absent: number; pending: number } | null;
  setBadgeCounts: React.Dispatch<
    React.SetStateAction<{
      present: number;
      absent: number;
      pending: number;
    } | null>
  >;
  kbStudentIds: Set<string>;
  kbStudentTimes: Map<string, { start: string; end: string }>;
  /** Students already present (or late) earlier today per the bundle's prefill data. */
  earlierPresentIds: Set<string>;

  // Concurrent course attendance
  concurrentCourseAttendance: ConcurrentCourseAttendance;
  setConcurrentCourseAttendance: React.Dispatch<
    React.SetStateAction<ConcurrentCourseAttendance>
  >;

  // Next-period concurrent courses (for Doppelstunde)
  nextLessonCourses: Array<{
    course_name: string;
    room_name?: string;
    teacher_names?: string;
    is_cancelled?: boolean;
    students: Array<{ id: string; name: string }>;
  }>;
  nextLessonIsCancelled: boolean;
  nextLessonSubject: string | null;

  // Students kept in main lesson (for course popup banner).
  // mode='present' → confirmed by the home teacher (SAL row exists on the
  // main lesson). mode='pending' → still Ausstehend; only a
  // lesson_guest_students(kind='transferred_back') row exists, the home
  // teacher hasn't yet recorded attendance on the main lesson.
  keptInMainLesson: Array<{
    id: string;
    name: string;
    notes: string;
    fromLessonId: string | null;
    mode: "present" | "pending";
  }>;

  // Reload trigger (for post-transfer refresh)
  reloadBundle: () => Promise<void>;

  // Transfer UI
  pendingTransfer: {
    studentId: string;
    studentName: string;
    courseName: string;
    courseLessonId: string;
  } | null;
  setPendingTransfer: React.Dispatch<
    React.SetStateAction<{
      studentId: string;
      studentName: string;
      courseName: string;
      courseLessonId: string;
    } | null>
  >;
  transferReason: string;
  setTransferReason: React.Dispatch<React.SetStateAction<string>>;
  transferredStudentIds: Set<string>;
  setTransferredStudentIds: React.Dispatch<React.SetStateAction<Set<string>>>;

  // Frozen sort order
  frozenEditOrderRef: React.MutableRefObject<string[]>;
  frozenEditLessonIdRef: React.MutableRefObject<string | null>;

  // Realtime suppression — exposed so the save hook can flip it on around
  // its writes, preventing the loader's bundle realtime subscription from
  // re-fetching for SAL inserts the save itself just made.
  suppressRealtimeRef: React.MutableRefObject<boolean>;
  /**
   * Pause the loader's bundle realtime subscription for `ms` milliseconds.
   * Use this from the save flow instead of mutating `suppressRealtimeRef`
   * directly so a pending release timer (e.g. from the load effect) doesn't
   * pre-empt the save's pause.
   */
  pauseRealtime: (ms: number) => void;

  // RTK Query hooks
  triggerGetBundle: ReturnType<typeof useLazyGetAttendanceBundleQueryQuery>[0];
  transferStudent: ReturnType<typeof useTransferStudentMutation>[0];
  isTransferring: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useAttendanceLoader({
  open,
  lessonId,
  viewMode,
  initialBundle,
  courseOverlaps,
  schoolIdOverride,
  userIdOverride,
}: UseAttendanceLoaderArgs): UseAttendanceLoaderReturn {
  // ---------- State ----------
  const [selectedLesson, setSelectedLesson] = useState<any | null>(null);
  const [attendanceViewMode, setAttendanceViewMode] = useState<
    "overview" | "edit"
  >(viewMode);
  const [overviewData, setOverviewData] = useState<any>(null);
  // Suppress realtime handler during explicit reloads to avoid duplicate fetches
  const suppressRealtimeRef = useRef(false);
  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [lessonNote, setLessonNote] = useState("");
  const [lessonHomework, setLessonHomework] = useState("");
  const [initialLessonHomework, setInitialLessonHomework] = useState("");
  const [tempAttendance, setTempAttendance] = useState<TempAttendanceMap>({});
  const explicitPresentIdsRef = useRef<Set<string>>(new Set());
  const [editedStudentIds, setEditedStudentIds] = useState<Set<string>>(
    new Set(),
  );

  // Protection & auxiliary
  const [protectionByStudent, setProtectionByStudent] = useState<
    Record<
      string,
      {
        protected: boolean;
        reasons: string[];
        hasAnyToday: boolean;
        isAbsentToday: boolean;
      }
    >
  >({});
  const [overrideReasons, setOverrideReasons] = useState<
    Record<string, string>
  >({});
  const [lessonStatusOverrides] = useState<
    Record<string, { status?: string; note?: string }>
  >({});
  const [prefilledFromPrevious, setPrefilledFromPrevious] = useState(false);
  const [prefilledAny, setPrefilledAny] = useState(false);
  const [recordedMeta, setRecordedMeta] = useState<{
    name: string;
    atISO: string;
  } | null>(null);
  const [diaryMeta, setDiaryMeta] = useState<{
    name: string;
    atISO: string;
  } | null>(null);
  const [canEditExisting, setCanEditExisting] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<{
    present: number;
    absent: number;
    pending: number;
  } | null>(null);
  const [kbStudentIds, setKbStudentIds] = useState<Set<string>>(new Set());
  const [kbStudentTimes, setKbStudentTimes] = useState<
    Map<string, { start: string; end: string }>
  >(new Map());

  // Students who were already present (or late) earlier today, per the bundle's
  // daily-prefill data. Used by EditSection to ask the teacher whether a student
  // newly marked absent has left the school or is in another offer.
  const [earlierPresentIds, setEarlierPresentIds] = useState<Set<string>>(
    new Set(),
  );

  // Concurrent course attendance
  const [concurrentCourseAttendance, setConcurrentCourseAttendance] =
    useState<ConcurrentCourseAttendance>({ courses: [] });

  // Next-period concurrent courses (for double lesson / Doppelstunde)
  const [nextLessonCourses, setNextLessonCourses] = useState<
    Array<{ course_name: string; room_name?: string; teacher_names?: string; is_cancelled?: boolean; students: Array<{ id: string; name: string }> }>
  >([]);
  const [nextLessonIsCancelled, setNextLessonIsCancelled] = useState(false);
  const [nextLessonSubject, setNextLessonSubject] = useState<string | null>(null);
  // Students kept in main lesson (shown as banner on course popups)
  const [keptInMainLesson, setKeptInMainLesson] = useState<
    Array<{
      id: string;
      name: string;
      notes: string;
      fromLessonId: string | null;
      mode: "present" | "pending";
    }>
  >([]);

  // Transfer UI
  const [pendingTransfer, setPendingTransfer] = useState<{
    studentId: string;
    studentName: string;
    courseName: string;
    courseLessonId: string;
  } | null>(null);
  const [transferReason, setTransferReason] = useState("");
  const [transferredStudentIds, setTransferredStudentIds] = useState<
    Set<string>
  >(new Set());

  // Frozen sort order
  const frozenEditOrderRef = useRef<string[]>([]);
  const frozenEditLessonIdRef = useRef<string | null>(null);

  // Atomic pause: cancels any existing release timer, sets the suppress
  // flag, and schedules a new release. Save flow calls this so its pause
  // doesn't get pre-empted by a release timer the loader scheduled earlier
  // (e.g. the 1s post-initial-load timer firing mid-save).
  const pauseRealtime = useCallback((ms: number) => {
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressRealtimeRef.current = true;
    suppressTimerRef.current = setTimeout(() => {
      suppressRealtimeRef.current = false;
      suppressTimerRef.current = null;
    }, ms);
  }, []);

  // ---------- Auth & RTK Query ----------
  const { userProfile } = useAuth();

  const [triggerGetBundle] = useLazyGetAttendanceBundleQueryQuery();
  const [transferStudent, { isLoading: isTransferring }] =
    useTransferStudentMutation();

  const fetchGuestStudentIds = useCallback(async (targetLessonId: string) => {
    try {
      const { data, error } = await supabase.rpc(
        "fn_attendance_guest_student_ids" as never,
        { p_lesson_id: targetLessonId } as never,
      );
      if (error) return new Set<string>();
      const ids = Array.isArray((data as any)?.student_ids)
        ? ((data as any).student_ids as string[])
        : [];
      return new Set(ids.map((id) => String(id)));
    } catch {
      return new Set<string>();
    }
  }, []);

  // ---------- Load Effect ----------
  useEffect(() => {
    const load = async () => {
      if (!open || !lessonId) return;
      try {
        pauseRealtime(1000);
        setAttendanceViewMode(viewMode);
        setAttendanceLoading(true);
        setOverviewData(null);
        setTempAttendance({});
        setEditedStudentIds(new Set());
        setLessonNote("");
        setLessonHomework("");
        setInitialLessonHomework("");
        setRecordedMeta(null);
        setDiaryMeta(null);
        setBadgeCounts(null);
        setCanEditExisting(false);
        setKbStudentIds(new Set());
        setEarlierPresentIds(new Set());
        setNextLessonCourses([]);
        setNextLessonIsCancelled(false);
        setNextLessonSubject(null);
        setKeptInMainLesson([]);
        setTransferredStudentIds(new Set());

        // Prefer the explicit overrides (kiosk path supplies these from
        // the chip-login session) over the React auth context. For the
        // authenticated browser-tab path the overrides will be undefined
        // and we fall back to userProfile, same as before.
        const schoolId = schoolIdOverride ?? (userProfile as any)?.school_id ?? null;
        const userId   = userIdOverride   ?? (userProfile as any)?.id        ?? null;

        if (!schoolId && !initialBundle) {
          console.warn("No school_id in auth context");
          setAttendanceLoading(false);
          return;
        }

        let bundle: AttendanceBundle;
        if (initialBundle) {
          bundle = initialBundle;
        } else {
          // preferCacheValue=false: always fetch fresh on open. The cache
          // would otherwise serve stale data after a save (we removed the
          // post-save invalidateTags to avoid the lazy subscription
          // triggering an immediate background refetch the dialog can't
          // even consume — it's already closing).
          const result = await triggerGetBundle(
            { lessonId, schoolId: schoolId!, userId },
            false,
          ).unwrap();
          bundle = result;
        }

        if (!bundle?.lesson) {
          console.warn("No lesson data in bundle");
          setAttendanceLoading(false);
          return;
        }

        // Build lesson object from bundle
        const lessonData = bundle.lesson;
        const guestStudentIds =
          lessonData.course_id && lessonId
            ? await fetchGuestStudentIds(lessonId)
            : new Set<string>();
        const startTime = lessonData.start_datetime
          ? new Date(lessonData.start_datetime)
          : new Date();
        const endTime = lessonData.end_datetime
          ? new Date(lessonData.end_datetime)
          : new Date();

        const lesson = {
          id: lessonData.lesson_id,
          school_id: lessonData.school_id,
          class_id: lessonData.class_id || null,
          course_id: lessonData.course_id || null,
          is_course: !!lessonData.course_id,
          time: new Intl.DateTimeFormat("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Europe/Berlin",
          }).format(startTime),
          endTime: new Intl.DateTimeFormat("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Europe/Berlin",
          }).format(endTime),
          start_datetime: lessonData.start_datetime,
          end_datetime: lessonData.end_datetime,
          block_number: lessonData.period_number,
          subject: lessonData.course_id
            ? lessonData.course_name || lessonData.subject_name || "Flexmodul"
            : lessonData.subject_name || "Unbekannt",
          class: lessonData.course_id ? "" : lessonData.class_name || "",
          room: lessonData.room_name,
          location: lessonData.room_name,
          isCurrent: false,
          isSubstitute: lessonData.lesson_type === "substitute",
          isCancelled: lessonData.is_cancelled || false,
          students: (() => {
            const excludedIds = new Set<string>();
            const isCourse = !!lessonData.course_id;

            // Always exclude guests-elsewhere — they are physically in
            // another room today (Tagesgast), so the original course/class
            // teacher should not be able to mark them present/absent here.
            for (const ge of bundle.guests_elsewhere || []) {
              excludedIds.add(ge.student_id);
            }

            if (isCourse) {
              // COURSE lesson: exclude students transferred back to main lesson
              const ml = bundle.concurrent_course_attendance?.main_lesson;
              if (ml?.students_with_main_attendance?.length) {
                for (const s of ml.students_with_main_attendance) {
                  excludedIds.add(s.id);
                }
              }
              // Also exclude students this course pushed back to the home
              // class as pending (kind='transferred_back'). They no longer
              // belong in the course roster — the home teacher owns them.
              for (const sid of bundle.transferred_away_student_ids ?? []) {
                excludedIds.add(sid);
              }
            } else {
              // CLASS lesson: exclude students enrolled in concurrent courses
              // Always use the RPC data — never rely on courseOverlaps prop
              // Skip cancelled courses — their students should stay in the main list
              const rpcCourses = (
                bundle.concurrent_course_attendance?.courses || []
              ).filter((c: any) => !c.is_cancelled);
              if (rpcCourses.length) {
                const mainAttendanceIds = new Set<string>();
                for (const s of bundle.attendance.present)
                  mainAttendanceIds.add(s.student_id);
                for (const s of bundle.attendance.late)
                  mainAttendanceIds.add(s.student_id);
                for (const s of bundle.attendance.absent)
                  mainAttendanceIds.add(s.student_id);
                // Un-exclude students who have a kind='transferred_back'
                // row for THIS home lesson — the course teacher pushed
                // them back as Ausstehend, so they belong in the home
                // roster waiting for the home teacher to confirm.
                const pendingBackIds = new Set<string>(
                  bundle.pending_transfer_back_ids ?? [],
                );
                for (const co of rpcCourses as Array<{
                  students: Array<{ id: string }>;
                }>) {
                  for (const s of co.students) {
                    if (mainAttendanceIds.has(s.id)) continue;
                    if (pendingBackIds.has(s.id)) continue;
                    excludedIds.add(s.id);
                  }
                }
              }
            }
            return bundle.students
              .filter((s) => !excludedIds.has(s.id))
              .map((s) => ({
                id: s.id,
                name: s.name,
                className: s.class_name || "",
                isGuest: guestStudentIds.has(String(s.id)),
              }));
          })(),
          enrolled: (() => {
            let count = bundle.students.length;
            const isCourse = !!lessonData.course_id;

            // Guests-elsewhere are physically not in this room, so they
            // shouldn't count toward the enrolled total either.
            count = Math.max(
              0,
              count - (bundle.guests_elsewhere?.length || 0),
            );

            if (isCourse) {
              // COURSE lesson: subtract students transferred back to main lesson
              const ml = bundle.concurrent_course_attendance?.main_lesson;
              if (ml?.students_with_main_attendance?.length) {
                count = Math.max(
                  0,
                  count - ml.students_with_main_attendance.length,
                );
              }
              // Mirror the roster exclusion: pending-transferred students
              // aren't part of the course's enrolled count either.
              const _ta = bundle.transferred_away_student_ids ?? [];
              if (_ta.length) {
                count = Math.max(0, count - _ta.length);
              }
            } else {
              // CLASS lesson: subtract students in concurrent courses
              // Always use RPC data, skip cancelled courses
              const rpcCourses = (
                bundle.concurrent_course_attendance?.courses || []
              ).filter((c: any) => !c.is_cancelled);
              if (rpcCourses.length) {
                const mainAttendanceIds = new Set<string>();
                for (const s of bundle.attendance.present)
                  mainAttendanceIds.add(s.student_id);
                for (const s of bundle.attendance.late)
                  mainAttendanceIds.add(s.student_id);
                for (const s of bundle.attendance.absent)
                  mainAttendanceIds.add(s.student_id);
                // Match the roster-exclusion logic above: transferred-back
                // students are NOT excluded from the enrolled count
                // because they belong in the home roster.
                const pendingBackIds = new Set<string>(
                  bundle.pending_transfer_back_ids ?? [],
                );
                const excludedCount = (
                  rpcCourses as Array<{
                    students: Array<{ id: string }>;
                  }>
                ).reduce((sum, co) => {
                  const notTransferred = co.students.filter(
                    (s) =>
                      !mainAttendanceIds.has(s.id) &&
                      !pendingBackIds.has(s.id),
                  );
                  return sum + notTransferred.length;
                }, 0);
                count = Math.max(0, count - excludedCount);
              }
            }
            return count;
          })(),
          attendance: bundle.attendance,
          attendanceTaken:
            bundle.attendance.present.length +
              bundle.attendance.late.length +
              bundle.attendance.absent.length >
            0 || !!bundle.diary?.is_course_short_notice_cancelled,
          course_short_notice_cancelled:
            !!bundle.diary?.is_course_short_notice_cancelled,
          course_short_notice_reason:
            bundle.diary?.is_course_short_notice_cancelled
              ? bundle.diary?.text || null
              : null,
          primary_teacher_id: (lessonData as any).primary_teacher_id || null,
          parallel_main_lesson_id:
            lessonData.course_id &&
            bundle.concurrent_course_attendance?.main_lesson?.lesson_id
              ? bundle.concurrent_course_attendance.main_lesson.lesson_id
              : null,
          // Class-distribution guests folded in by get_attendance_bundle_v2.
          guests: (bundle as { guests?: any[] }).guests || [],
          guests_elsewhere: bundle.guests_elsewhere || [],
          // Used by EditSection to gate BandLogEntryForm mounting so the
          // form's 4 RPC queries don't fire on lessons with no band.
          has_band_assignment: !!(bundle as any).has_band_assignment,
          // Pre-bundled band data — when present the BandLogEntryForm reads
          // from this instead of firing its 4 RPCs.
          band_data: (bundle as any).band_data,
          // Klassenbuch week-approval lock, surfaced by get_attendance_bundle_v2
          // so the dialog can mirror the server-side write trigger.
          kb_locked: !!(lessonData as any).kb_locked,
          kb_approved_by_name:
            (lessonData as any).kb_approved_by_name ?? null,
          kb_approved_at: (lessonData as any).kb_approved_at ?? null,
        };

        setSelectedLesson(lesson);
        setLessonNote(bundle.diary?.text || "");
        // Capture initial homework so the save hook can compare and skip
        // the diary-upsert RPC when the field wasn't actually edited.
        {
          const hw = bundle.diary?.homework ?? "";
          setLessonHomework(hw);
          setInitialLessonHomework(hw);
        }

        // Badge counts — always use RPC data for exclusion
        if (bundle.badge_counts) {
          const present =
            (bundle.badge_counts.present || 0) +
            (bundle.badge_counts.late || 0);
          const absent = bundle.badge_counts.absent || 0;
          let total = bundle.badge_counts.total_students || 0;
          const isCourse = !!lessonData.course_id;

          // Guests-elsewhere don't count toward this room's total.
          total = Math.max(
            0,
            total - (bundle.guests_elsewhere?.length || 0),
          );

          if (isCourse) {
            // COURSE lesson: subtract transferred-back students
            const ml = bundle.concurrent_course_attendance?.main_lesson;
            if (ml?.students_with_main_attendance?.length) {
              total = Math.max(
                0,
                total - ml.students_with_main_attendance.length,
              );
            }
            // …and pending-transferred-away students (no SAL on main).
            const _ta = bundle.transferred_away_student_ids ?? [];
            if (_ta.length) {
              total = Math.max(0, total - _ta.length);
            }
          } else {
            // CLASS lesson: subtract concurrent course students (skip cancelled)
            const rpcCourses = (
              bundle.concurrent_course_attendance?.courses || []
            ).filter((c: any) => !c.is_cancelled);
            if (rpcCourses.length) {
              const mainAttendanceIds = new Set<string>();
              for (const s of bundle.attendance.present)
                mainAttendanceIds.add(s.student_id);
              for (const s of bundle.attendance.late)
                mainAttendanceIds.add(s.student_id);
              for (const s of bundle.attendance.absent)
                mainAttendanceIds.add(s.student_id);
              // Badge counts also need to keep transferred-back students
              // in the home roster total, matching the dialog's roster.
              const pendingBackIds = new Set<string>(
                bundle.pending_transfer_back_ids ?? [],
              );
              const excludedCount = (
                rpcCourses as Array<{
                  students: Array<{ id: string }>;
                }>
              ).reduce((sum, co) => {
                const notTransferred = co.students.filter(
                  (s) =>
                    !mainAttendanceIds.has(s.id) &&
                    !pendingBackIds.has(s.id),
                );
                return sum + notTransferred.length;
              }, 0);
              total = Math.max(0, total - excludedCount);
            }
          }
          const pendingCalc = Math.max(0, total - (present + absent));
          setBadgeCounts({ present, absent, pending: pendingCalc });
        }

        // Bug 2: Course lessons should NOT show the concurrent panel at all.
        // Only class lessons show parallel course info.
        if (lessonData.course_id) {
          setConcurrentCourseAttendance({ courses: [] });
          // Pre-populate transferredStudentIds from RPC data so the UI
          // correctly reflects students pulled back to the main lesson.
          const ml = bundle.concurrent_course_attendance?.main_lesson;
          const transferredAwayIds = bundle.transferred_away_student_ids ?? [];
          // Lookup names from the raw bundle.students (unfiltered) so
          // pending-transferred students still show with their real names
          // in the "Im Klassenunterricht geblieben" banner even though
          // they're excluded from the active roster.
          const bundleStudentNameById = new Map<string, string>();
          for (const s of bundle.students || []) {
            if (s?.id) bundleStudentNameById.set(s.id, s.name || "");
          }
          if (
            ml?.students_with_main_attendance?.length ||
            transferredAwayIds.length
          ) {
            const transferredIdSet = new Set<string>([
              ...(ml?.students_with_main_attendance ?? []).map(
                (s: any) => s.id,
              ),
              ...transferredAwayIds,
            ]);
            setTransferredStudentIds(transferredIdSet);
            // Populate kept-in-main-lesson banner data for course popups —
            // includes both the legacy "transferred with main SAL" case
            // AND the new "pending transfer-back" case.
            const fromMain = (ml?.students_with_main_attendance ?? []).map(
              (s: any) => ({
                id: s.id,
                name: s.name || "",
                notes: s.notes || "",
                fromLessonId: ml?.lesson_id || null,
                mode: "present" as const,
              }),
            );
            const fromPending = transferredAwayIds
              .filter(
                (sid) =>
                  !fromMain.some((entry) => entry.id === sid),
              )
              .map((sid) => ({
                id: sid,
                name: bundleStudentNameById.get(sid) || "",
                notes: "",
                fromLessonId: ml?.lesson_id || null,
                mode: "pending" as const,
              }));
            setKeptInMainLesson([...fromMain, ...fromPending]);
          }
        } else {
          setConcurrentCourseAttendance(
            bundle.concurrent_course_attendance || { courses: [] },
          );
          // For class lessons: mark students as "transferred" only if they
          // have main lesson attendance AND no course attendance (status is null).
          // Students with attendance in BOTH places are legitimate and stay visible.
          const rpcCourses = bundle.concurrent_course_attendance?.courses || [];
          const pendingBackIdsForClass = new Set<string>(
            bundle.pending_transfer_back_ids ?? [],
          );
          if (rpcCourses.length || pendingBackIdsForClass.size > 0) {
            const mainAttIds = new Set<string>();
            for (const s of bundle.attendance.present)
              mainAttIds.add(s.student_id);
            for (const s of bundle.attendance.late)
              mainAttIds.add(s.student_id);
            for (const s of bundle.attendance.absent)
              mainAttIds.add(s.student_id);
            const transferred = new Set<string>();
            for (const co of rpcCourses as Array<{
              students: Array<{ id: string; status: string | null }>;
            }>) {
              for (const s of co.students) {
                // Only hide if student has main attendance but NO course attendance
                if (mainAttIds.has(s.id) && s.status == null) {
                  transferred.add(s.id);
                }
              }
            }
            // Pending-transferred-back students are now in the home roster
            // (un-excluded by the loader above). They must also be marked
            // as "transferred" so the ConcurrentCoursePanel filters them
            // out of the "heute nicht" duplicate listing.
            for (const sid of pendingBackIdsForClass) transferred.add(sid);
            if (transferred.size > 0) setTransferredStudentIds(transferred);
          }
        }

        // Recorder meta
        if (bundle.recorder_meta?.recorded_by && bundle.recorder_meta?.at) {
          setRecordedMeta({
            name: bundle.recorder_meta.name || "Unbekannt",
            atISO: bundle.recorder_meta.at,
          });
        }

        // Diary author meta
        if (bundle.diary && String(bundle.diary.text || "").trim()) {
          setDiaryMeta({
            name: bundle.diary.created_by_name || "Unbekannt",
            atISO: bundle.diary.created_at || "",
          });
        }

        // Edit permission
        const isAdminOrSuper =
          userProfile?.role === "Admin" || userProfile?.role === "Super Admin";
        const canEdit = isAdminOrSuper || bundle.edit_permission.can_edit;
        setCanEditExisting(canEdit);

        // Helper functions
        const extractArrivalTime = (record: any): string => {
          if (record.arrival_time) return record.arrival_time;
          if (record.notes) {
            const arrivalMatch = record.notes.match(/Ankunft:\s*(\d{2}:\d{2})/);
            if (arrivalMatch) return arrivalMatch[1];
          }
          return getDefaultLateTime(lesson);
        };
        const getActualLateMinutes = (record: any): number => {
          if (record.lateness_duration_minutes)
            return record.lateness_duration_minutes;
          if (record.late_minutes) return record.late_minutes;
          if (record.notes) {
            const minutesMatch = record.notes.match(
              /(\d+)\s*Min\.\s*verspätet/,
            );
            if (minutesMatch) return parseInt(minutesMatch[1], 10);
          }
          return 5;
        };
        const isLateExcused = (record: any): boolean => {
          if (record.late_excused === true) return true;
          if (record.notes && record.notes.includes("Entschuldigt"))
            return true;
          return false;
        };

        const attendanceData = bundle.attendance || {
          present: [],
          late: [],
          absent: [],
        };
        // Always populate overviewData so the future-preview render path
        // (which forces OverviewSection regardless of viewMode for future
        // lessons) has data to render. Edit-mode tempAttendance is built
        // separately below when viewMode !== "overview".
        {
          const getUiStudentNameById = (sid: string): string => {
            const s = (lesson?.students || []).find(
              (stu: any) => stu.id === sid,
            );
            return s?.name || "";
          };
          const getUiStudentClassById = (sid: string): string => {
            const s = (lesson?.students || []).find(
              (stu: any) => stu.id === sid,
            );
            return s?.className || "";
          };
          // Audit #23: thread the guest flag through to the OVERVIEW too.
          // `lesson.students` was built above with `isGuest:
          // guestStudentIds.has(...)` from fn_attendance_guest_student_ids,
          // but the present/late/absent overview rows weren't carrying it.
          // This is the INITIAL-OPEN path; the second setOverviewData around
          // line 1509 (post-save refresh) had the same gap and is fixed in
          // the matching block down there.
          const isGuestById = new Set<string>(
            (lesson?.students || [])
              .filter((s: any) => s?.isGuest)
              .map((s: any) => String(s.id)),
          );
          const getIsGuestById = (sid: string): boolean =>
            isGuestById.has(String(sid));
          const late = attendanceData.late.map((r: any) => ({
            id: r.student_id,
            name: getUiStudentNameById(r.student_id),
            className: getUiStudentClassById(r.student_id),
            minutesLate: getActualLateMinutes(r),
            arrivalTime: extractArrivalTime(r),
            lateExcused: isLateExcused(r),
            isGuest: getIsGuestById(r.student_id),
          }));
          const absent = attendanceData.absent.map((r: any) => ({
            id: r.student_id,
            name: getUiStudentNameById(r.student_id),
            className: getUiStudentClassById(r.student_id),
            excused: r.status === "absent_excused",
            // absent_unclear epic: distinguish pre-klärung from formal
            // absent_unexcused so the OverviewSection can render the
            // orange "Ungeklärt" badge instead of red "Unentschuldigt".
            unclear: r.status === "absent_unclear",
            reason: r.notes || "",
            isGuest: getIsGuestById(r.student_id),
          }));
          const explicitPresentIds = new Set<string>(
            attendanceData.present.map((r: any) =>
              typeof r === "string" ? r : r.student_id,
            ),
          );
          explicitPresentIdsRef.current = explicitPresentIds;
          const lateIds = new Set<string>(late.map((s: any) => s.id));
          const absentIds = new Set<string>(absent.map((s: any) => s.id));
          const inferredPresent: {
            id: string;
            name: string;
            className: string;
            isGuest: boolean;
          }[] = [];
          (lesson?.students || []).forEach((stu: any) => {
            const sid = String(stu.id);
            if (absentIds.has(sid) || lateIds.has(sid)) return;
            if (explicitPresentIds.has(sid)) return;
            inferredPresent.push({
              id: sid,
              name: getUiStudentNameById(sid),
              className: getUiStudentClassById(sid),
              isGuest: getIsGuestById(sid),
            });
          });
          const presentExplicit = attendanceData.present.map((r: any) => {
            const sid = typeof r === "string" ? r : r.student_id;
            return {
              id: sid,
              name: getUiStudentNameById(sid),
              className: getUiStudentClassById(sid),
              notes: typeof r === "object" ? r.notes || "" : "",
              isGuest: getIsGuestById(sid),
            };
          });
          const present = [...presentExplicit];
          present.sort((a: any, b: any) => compareNames(a.name, b.name));
          inferredPresent.sort((a: any, b: any) =>
            compareNames(a.name, b.name),
          );
          late.sort((a: any, b: any) => compareNames(a.name, b.name));
          absent.sort((a: any, b: any) => compareNames(a.name, b.name));
          setOverviewData({
            lessonNote: bundle?.diary?.text || "",
            present,
            late,
            absent,
            pending: inferredPresent,
          });
        }

        if (viewMode !== "overview") {
          const editAttendance: TempAttendanceMap = {};
          const addIf = (sid?: string, obj?: any) => {
            if (sid) editAttendance[sid] = obj;
          };
          attendanceData.present.forEach((record: any) =>
            addIf(record.student_id, { status: "present" }),
          );
          attendanceData.late.forEach((record: any) =>
            addIf(record.student_id, {
              status: "late",
              minutesLate: getActualLateMinutes(record),
              arrivalTime: extractArrivalTime(record),
              lateExcused: isLateExcused(record),
            }),
          );
          attendanceData.absent.forEach((record: any) => {
            // Parse "Anderswo: <location>" prefix from the saved note so
            // re-opening a previously-saved row restores the elsewhereLocation
            // flag. Without this, the earlier-present save guard re-asks
            // "Schule verlassen oder anderswo?" on every reopen, and the
            // double-lesson carry-over wouldn't recognize the student as
            // Anderswo (so they'd stay absent instead of flipping to present).
            const noteStr = String(record.notes || "");
            const anderswoMatch = noteStr.match(
              /(?:^|\|\s*)Anderswo:\s*([^|]+?)\s*(?:\||$)/i,
            );
            if (anderswoMatch) {
              addIf(record.student_id, {
                status:
                  record.status === "absent_excused" ? "excused" : "unexcused",
                excuseReason: noteStr,
                elsewhereLocation: anderswoMatch[1].trim(),
              });
              return;
            }
            addIf(record.student_id, {
              status:
                record.status === "absent_excused" ? "excused" : "unexcused",
              excuseReason: record.notes || "",
            });
          });
          // Prefills from bundle
          (bundle?.prefills?.krank || []).forEach((item: any) => {
            const sid = typeof item === "string" ? item : item?.student_id;
            const reason = typeof item === "object" ? item?.reason || "" : "";
            if (sid && !editAttendance[sid])
              editAttendance[sid] = { status: "excused", excuseReason: reason };
          });
          (bundle?.prefills?.daily_absent_excused || []).forEach(
            (sid: string) => {
              if (!editAttendance[sid])
                editAttendance[sid] = { status: "excused", excuseReason: "" };
            },
          );
          // absent_unclear epic: bundle previously surfaced only
          // `daily_absent_unexcused`; with the enum split, pre-klärung
          // kids are in `daily_absent_unclear`. Read from both arrays
          // so the prefill keeps working before + after the RPC update.
          const allAbsentPrefill = [
            ...((bundle?.prefills as any)?.daily_absent_unclear || []),
            ...((bundle?.prefills as any)?.daily_absent_unexcused || []),
          ];
          allAbsentPrefill.forEach((sid: string) => {
            if (!editAttendance[sid])
              editAttendance[sid] = { status: "unexcused" };
          });
          // Pre-fill checked-out students as excused with their checkout note
          // Only include students who checked out BEFORE this lesson started
          const checkedOutItems: Array<{ student_id: string; note?: string; check_out_time?: string }> =
            (bundle.protections?.checked_out || []).map((item: any) =>
              typeof item === "string" ? { student_id: item } : item
            ).filter((item: any) => {
              if (!item.check_out_time) return true; // no time info → include (safety)
              const checkoutTime = new Date(item.check_out_time);
              return checkoutTime < startTime; // only if checked out before lesson start
            });
          checkedOutItems.forEach(({ student_id: sid, note }) => {
            if (!editAttendance[sid]) {
              // Extract readable text from checkout:<StaffName>:<text> tag
              let reason = "Ausgecheckt";
              if (note) {
                const match = note.match(/checkout:[^:]*:(.+)/s);
                if (match) reason = `Ausgecheckt: ${match[1].trim()}`;
              }
              editAttendance[sid] = { status: "excused", excuseReason: reason };
            }
          });
          // "Already recorded" means a person genuinely saved this
          // lesson. Auto/system logs — a day-level krank absence, a
          // kiosk pre-Klärung `absent_unclear` row — have no recorder
          // and must NOT suppress the carry-forward prefill.
          const hasExistingAttendance = hasGenuineAttendance(
            bundle?.recorder_meta,
          );
          let usedPrevious = false;
          // Apply carry-forward prefills per-student — never overwriting a
          // student who already has a real SAL row (the inner `editAttendance[sid]`
          // check enforces that). The previous outer
          // `if (!hasExistingAttendance)` gate dropped carry-forward for
          // the WHOLE roster the moment a single SAL row was written
          // (e.g. a "heute nicht"/transfer flipping one student to present),
          // leaving the other 16 students with no prefill at all.
          (bundle?.prefills?.previous_lesson_status || []).forEach(
            (row: any) => {
              if (editAttendance[row.student_id]) return;
              const noteStr = String(row.notes || "");
              const wasAnderswo = /(^|\|\s*)Anderswo:/i.test(noteStr);
              if (row.status === "present") {
                editAttendance[row.student_id] = { status: "present" };
                usedPrevious = true;
                return;
              }
              if (row.status === "late") {
                editAttendance[row.student_id] = { status: "present" };
                usedPrevious = true;
                return;
              }
              if (row.status === "absent_excused") {
                // Anderswo means the student was elsewhere in the school
                // last lesson — assume they're back for this one.
                if (wasAnderswo) {
                  editAttendance[row.student_id] = { status: "present" };
                  usedPrevious = true;
                  return;
                }
                editAttendance[row.student_id] = {
                  status: "excused",
                  excuseReason: row.notes || "",
                };
                usedPrevious = true;
                return;
              }
              if (
                row.status === "absent_unexcused" ||
                row.status === "absent_unclear"
              ) {
                editAttendance[row.student_id] = { status: "unexcused" };
                usedPrevious = true;
                return;
              }
            },
          );
          setPrefilledFromPrevious(usedPrevious);
          // Students transferred back to this (home) lesson as pending
          // must NOT be auto-prefilled as present — the home teacher
          // decides whether they made it back. Leave them ausstehend.
          const pendingBackPrefillSkip = new Set<string>(
            bundle.pending_transfer_back_ids ?? [],
          );
          (bundle?.prefills?.daily_present || []).forEach((sid: string) => {
            if (pendingBackPrefillSkip.has(sid)) return;
            if (!editAttendance[sid])
              editAttendance[sid] = { status: "present" };
          });
          (bundle?.prefills?.daily_late || []).forEach((item: any) => {
            const sid = typeof item === "string" ? item : item?.student_id;
            if (sid && pendingBackPrefillSkip.has(sid)) return;
            if (sid && !editAttendance[sid]) {
              const at = item?.expected_arrival_time
                ? String(item.expected_arrival_time).slice(0, 5)
                : getDefaultLateTime(lesson);
              editAttendance[sid] = {
                status: "late",
                arrivalTime: at,
                minutesLate: calculateLateMinutes(at, lesson),
                lateExcused: false,
              };
            }
          });
          // Compute `prefilledAny` AFTER the daily_present / daily_late
          // prefills run. Doing it earlier (before this block) made the
          // "Anwesenheit wurde übernommen" banner miss the case where
          // daily_present is the *only* source of prefill — which is
          // exactly the post-fix shape of an orphan-row-cleared lesson,
          // and the case Lisa flagged.
          setPrefilledAny(
            usedPrevious || Object.keys(editAttendance).length > 0,
          );
          setTempAttendance(editAttendance);

          // Track who was already at school earlier on the lesson's day and
          // is *still* checked in. One prune vs the raw `daily_present` /
          // `daily_late` prefill:
          //   * Skip students already in `protections.checked_out` (kiosk-
          //     level checkout sets `check_out_time` without necessarily
          //     changing presence_status, so they can sit in both buckets).
          //     They're already out — no need to consider them earlier-
          //     present for the popup gating.
          //
          // The "is this lesson today?" gate that previously lived here was
          // moved to the per-popup call sites (EditSection: handleSaveAll's
          // Auschecken/Sonstiges guard). Earlier-present data is now always
          // populated from the lesson's date so the course-absent popup
          // works for past-day backfilling too (the teacher backfilling a
          // course on Friday still wants the "stayed with class?" prompt
          // when Noah was present that day).
          {
            const earlier = new Set<string>();

            const checkedOutIds = new Set<string>();
            for (const item of (bundle?.protections?.checked_out as any[]) ||
              []) {
              const sid = typeof item === "string" ? item : item?.student_id;
              if (sid) checkedOutIds.add(String(sid));
            }

            {
              (bundle?.prefills?.daily_present || []).forEach((sid: string) => {
                if (sid && !checkedOutIds.has(sid)) earlier.add(sid);
              });
              (bundle?.prefills?.daily_late || []).forEach((item: any) => {
                const sid = typeof item === "string" ? item : item?.student_id;
                if (sid && !checkedOutIds.has(sid)) earlier.add(sid);
              });
            }
            setEarlierPresentIds(earlier);
          }
        }

        // --- Kurzbeschulung overlay (from RPC, server-side filtered) ---
        const kbSet = new Set<string>();
        const kbOutsideSet = new Set<string>();
        const kbTimes = new Map<string, { start: string; end: string }>();
        if (bundle?.kb_overrides?.length && lesson.time && lesson.endTime) {
          for (const row of bundle.kb_overrides as any[]) {
            kbSet.add(row.student_id);
            if (
              isOutsideKBWindow(
                lesson.time,
                lesson.endTime,
                row.expected_start_time,
                row.expected_end_time,
              )
            ) {
              kbOutsideSet.add(row.student_id);
              kbTimes.set(row.student_id, {
                start: (row.expected_start_time || "").slice(0, 5),
                end: (row.expected_end_time || "").slice(0, 5),
              });
            }
          }
        }
        setKbStudentIds(kbOutsideSet);
        setKbStudentTimes(kbTimes);

        // IDs with a real saved attendance row for THIS lesson. The KB
        // cleanup below must never strip these — a genuinely-saved
        // `absent_excused` row with an empty note looks identical to a stale
        // KB prefill, so without this guard the loader silently drops real
        // attendance for any KB student on reload.
        const savedAttendanceIds = new Set<string>(
          [
            ...attendanceData.present.map((r: any) =>
              typeof r === "string" ? r : r.student_id,
            ),
            ...attendanceData.late.map((r: any) => r.student_id),
            ...attendanceData.absent.map((r: any) => r.student_id),
          ].filter(Boolean),
        );

        if (kbSet.size > 0 || kbOutsideSet.size > 0) {
          setTempAttendance((prev) => {
            const next = { ...prev };
            for (const sid of kbSet) {
              // Never strip a student who has a real saved row this lesson.
              if (savedAttendanceIds.has(sid)) continue;
              const entry = next[sid];
              if (
                entry &&
                entry.status === "excused" &&
                (entry.excuseReason === "Kurzbeschulung" || !entry.excuseReason)
              ) {
                delete next[sid];
              }
            }
            for (const sid of kbOutsideSet) {
              next[sid] = {
                status: "excused",
                excuseReason: "Kurzbeschulung",
              };
            }
            return next;
          });
        }

        // Protections from bundle
        if (bundle && lesson?.students) {
          const checkedOutSet = new Set<string>(
            (bundle.protections?.checked_out || []).filter((item: any) => {
              // Only include students who checked out BEFORE this lesson started
              const cot = typeof item === "string" ? null : item?.check_out_time;
              if (!cot) return typeof item === "string"; // legacy string format: include
              return new Date(cot) < startTime;
            }).map((item: any) =>
              typeof item === "string" ? item : item?.student_id
            ).filter(Boolean),
          );
          const krankSet = new Set<string>(
            (bundle.prefills?.krank || [])
              .map((item: any) =>
                typeof item === "string" ? item : item?.student_id,
              )
              .filter(Boolean),
          );
          const dailyExc = new Set<string>(
            bundle.prefills?.daily_absent_excused || [],
          );
          const dailyUnx = new Set<string>(
            bundle.prefills?.daily_absent_unexcused || [],
          );
          const dailyUnc = new Set<string>(
            (bundle.prefills as any)?.daily_absent_unclear || [],
          );
          const next: Record<
            string,
            {
              protected: boolean;
              reasons: string[];
              hasAnyToday: boolean;
              isAbsentToday: boolean;
            }
          > = {};
          for (const stu of lesson.students || []) {
            const sid = stu.id as string;
            const reasons: string[] = [];
            if (krankSet.has(sid)) reasons.push("krankgemeldet");
            if (dailyExc.has(sid)) reasons.push("daily_log.absent_excused");
            if (dailyUnx.has(sid)) reasons.push("daily_log.absent_unexcused");
            if (dailyUnc.has(sid)) reasons.push("daily_log.absent_unclear");
            if (checkedOutSet.has(sid)) reasons.push("checked_out");
            if (kbOutsideSet.has(sid)) reasons.push("kurzbeschulung");
            const isAbsentToday =
              krankSet.has(sid) ||
              dailyExc.has(sid) ||
              dailyUnx.has(sid) ||
              dailyUnc.has(sid) ||
              kbOutsideSet.has(sid) ||
              checkedOutSet.has(sid);
            next[sid] = {
              protected: reasons.length > 0,
              reasons,
              hasAnyToday:
                dailyExc.has(sid) ||
                dailyUnx.has(sid) ||
                dailyUnc.has(sid) ||
                checkedOutSet.has(sid),
              isAbsentToday,
            };
          }
          setProtectionByStudent(next);
        }

        // ---------- Next-period courses (Doppelstunde) ----------
        // Read directly from the bundle — the RPC detects consecutive lessons
        // and returns their concurrent course data in next_lesson_courses.
        const nextCourses = (bundle.next_lesson_courses || [])
          .filter((c: any) => c.students?.length > 0);
        if (nextCourses.length > 0) {
          setNextLessonCourses(nextCourses.map((c: any) => ({
            course_name: c.course_name,
            room_name: c.room_name,
            teacher_names: c.teacher_names,
            is_cancelled: !!c.is_cancelled,
            students: c.students,
          })));
        }

        // Consecutive lesson cancellation
        if (bundle.next_lesson_is_cancelled) {
          setNextLessonIsCancelled(true);
          setNextLessonSubject(bundle.next_lesson_subject || null);
        }
      } finally {
        setAttendanceLoading(false);
        // Re-arm the suppression window so the load's last DB writes don't
        // bounce back as a realtime refetch right after we finish.
        pauseRealtime(1000);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lessonId, viewMode]);

  // ---------- reloadBundle: imperative refresh for post-transfer ----------
  const reloadBundle = useCallback(async () => {
    if (!lessonId) return;
    // Kiosk path supplies these as overrides; authenticated path uses userProfile.
    const schoolId = schoolIdOverride ?? (userProfile as any)?.school_id;
    const userId   = userIdOverride   ?? (userProfile as any)?.id;
    pauseRealtime(2000);
    try {
      const result = await triggerGetBundle(
        {
          lessonId,
          schoolId,
          userId,
          flags: {
            include_kb_overrides: false,
            include_next_lesson: true,
            include_recorder_meta: false,
          },
        },
        false,
      ).unwrap();
      if (!result) return;
      const bundle = result as any;
      let refreshedStudents: Array<{
        id: string;
        name: string;
        className?: string;
      }> = (selectedLesson?.students || []) as Array<{
        id: string;
        name: string;
        className?: string;
      }>;

      // Rebuild selected lesson roster from fresh bundle so UI updates immediately
      // (e.g. after adding day-guest students) without closing/reopening dialog.
      if (bundle?.lesson) {
        const lessonData = bundle.lesson;
        const guestStudentIds =
          lessonData.course_id && lessonId
            ? await fetchGuestStudentIds(lessonId)
            : new Set<string>();
        const excludedIds = new Set<string>();
        const isCourse = !!lessonData.course_id;

        // Always exclude guests-elsewhere from the active roster (mirrors
        // initial-load logic).
        for (const ge of bundle.guests_elsewhere || []) {
          excludedIds.add(ge.student_id);
        }

        if (isCourse) {
          const ml = bundle.concurrent_course_attendance?.main_lesson;
          if (ml?.students_with_main_attendance?.length) {
            for (const s of ml.students_with_main_attendance) {
              excludedIds.add(s.id);
            }
          }
          // reloadBundle path: mirror the initial-load exclusion of
          // students this course pushed back to the home class as pending.
          for (const sid of ((bundle as any)
            .transferred_away_student_ids ?? []) as string[]) {
            excludedIds.add(sid);
          }
        } else {
          const rpcCourses = (
            bundle.concurrent_course_attendance?.courses || []
          ).filter((c: any) => !c.is_cancelled);
          if (rpcCourses.length) {
            const mainAttendanceIds = new Set<string>();
            for (const s of bundle.attendance.present)
              mainAttendanceIds.add(s.student_id);
            for (const s of bundle.attendance.late)
              mainAttendanceIds.add(s.student_id);
            for (const s of bundle.attendance.absent)
              mainAttendanceIds.add(s.student_id);
            // Mirror the initial-load exclusion: un-exclude transferred-
            // back students so they show up as Ausstehend in the home
            // roster after a reloadBundle (post-transfer or post-realtime).
            const pendingBackIds = new Set<string>(
              (bundle as any).pending_transfer_back_ids ?? [],
            );
            for (const co of rpcCourses as Array<{
              students: Array<{ id: string }>;
            }>) {
              for (const s of co.students) {
                if (mainAttendanceIds.has(s.id)) continue;
                if (pendingBackIds.has(s.id)) continue;
                excludedIds.add(s.id);
              }
            }
          }
        }

        const freshStudents = (bundle.students || [])
          .filter((s: any) => !excludedIds.has(s.id))
          .map((s: any) => ({
            id: s.id,
            name: s.name,
            className: s.class_name || "",
            isGuest: guestStudentIds.has(String(s.id)),
          }));
        refreshedStudents = freshStudents;

        let freshEnrolled = (bundle.students || []).length;
        // Subtract guests-elsewhere — they are not counted in this room.
        freshEnrolled = Math.max(
          0,
          freshEnrolled - (bundle.guests_elsewhere?.length || 0),
        );
        if (isCourse) {
          const ml = bundle.concurrent_course_attendance?.main_lesson;
          if (ml?.students_with_main_attendance?.length) {
            freshEnrolled = Math.max(
              0,
              freshEnrolled - ml.students_with_main_attendance.length,
            );
          }
          const _ta = ((bundle as any).transferred_away_student_ids ??
            []) as string[];
          if (_ta.length) {
            freshEnrolled = Math.max(0, freshEnrolled - _ta.length);
          }
        } else {
          const rpcCourses = (
            bundle.concurrent_course_attendance?.courses || []
          ).filter((c: any) => !c.is_cancelled);
          if (rpcCourses.length) {
            const mainAttendanceIds = new Set<string>();
            for (const s of bundle.attendance.present)
              mainAttendanceIds.add(s.student_id);
            for (const s of bundle.attendance.late)
              mainAttendanceIds.add(s.student_id);
            for (const s of bundle.attendance.absent)
              mainAttendanceIds.add(s.student_id);
            // reloadBundle path: same un-exclusion of transferred-back
            // students to match the dialog roster.
            const pendingBackIds = new Set<string>(
              (bundle as any).pending_transfer_back_ids ?? [],
            );
            const excludedCount = (
              rpcCourses as Array<{
                students: Array<{ id: string }>;
              }>
            ).reduce((sum, co) => {
              const notTransferred = co.students.filter(
                (s) =>
                  !mainAttendanceIds.has(s.id) &&
                  !pendingBackIds.has(s.id),
              );
              return sum + notTransferred.length;
            }, 0);
            freshEnrolled = Math.max(0, freshEnrolled - excludedCount);
          }
        }

        setSelectedLesson((prev: any) =>
          prev
            ? {
                ...prev,
                students: freshStudents,
                enrolled: freshEnrolled,
                attendance: bundle.attendance,
                attendanceTaken:
                  bundle.attendance.present.length +
                    bundle.attendance.late.length +
                    bundle.attendance.absent.length >
                  0 || !!bundle.diary?.is_course_short_notice_cancelled,
                course_short_notice_cancelled:
                  !!bundle.diary?.is_course_short_notice_cancelled,
                course_short_notice_reason:
                  bundle.diary?.is_course_short_notice_cancelled
                    ? bundle.diary?.text || null
                    : null,
                parallel_main_lesson_id:
                  prev.is_course &&
                  bundle.concurrent_course_attendance?.main_lesson?.lesson_id
                    ? bundle.concurrent_course_attendance.main_lesson.lesson_id
                    : prev.parallel_main_lesson_id ?? null,
                // Refresh class-distribution guests on bundle reload too
                guests: (bundle as { guests?: any[] }).guests || [],
                guests_elsewhere: bundle.guests_elsewhere || [],
              }
            : prev,
        );

        // Keep already edited statuses for existing students, drop removed ids.
        setTempAttendance((prev) => {
          const next: TempAttendanceMap = {};
          const allowed = new Set(freshStudents.map((s: any) => s.id));
          for (const [sid, val] of Object.entries(prev || {})) {
            if (allowed.has(sid)) next[sid] = val;
          }
          return next;
        });

        // Force edit list to recompute order so newly added students appear immediately.
        frozenEditLessonIdRef.current = null;
        frozenEditOrderRef.current = [];
      }

      // Update concurrent course attendance — mirror initial load normalization
      const isCourseLesson = !!(selectedLesson as any)?.is_course;
      if (isCourseLesson) {
        setConcurrentCourseAttendance({ courses: [] });
        // Derive kept-in-main-lesson from fresh bundle. Mirror the initial
        // load path so the "Im Klassenunterricht geblieben" banner picks up
        // pending-transfer-back students (no SAL on main → not in
        // students_with_main_attendance, only surfaced via the new
        // transferred_away_student_ids field). Without this the banner only
        // appears after a close+reopen of the dialog post-Ja-transfer.
        const ml = bundle.concurrent_course_attendance?.main_lesson;
        const transferredAwayIds =
          (bundle.transferred_away_student_ids ?? []) as string[];
        const bundleStudentNameById = new Map<string, string>();
        for (const s of bundle.students || []) {
          if (s?.id) bundleStudentNameById.set(s.id, s.name || "");
        }
        const fromMain = (ml?.students_with_main_attendance ?? []).map(
          (s: any) => ({
            id: s.id,
            name: s.name || "",
            notes: s.notes || "",
            fromLessonId: ml?.lesson_id || null,
            mode: "present" as const,
          }),
        );
        const fromPending = transferredAwayIds
          .filter(
            (sid) => !fromMain.some((entry: { id: string }) => entry.id === sid),
          )
          .map((sid) => ({
            id: sid,
            name: bundleStudentNameById.get(sid) || "",
            notes: "",
            fromLessonId: ml?.lesson_id || null,
            mode: "pending" as const,
          }));
        const combined = [...fromMain, ...fromPending];
        if (combined.length > 0) {
          setKeptInMainLesson(combined);
          setTransferredStudentIds(
            new Set(combined.map((entry) => entry.id)),
          );
        } else {
          setKeptInMainLesson([]);
        }
      } else if (bundle.concurrent_course_attendance) {
        setConcurrentCourseAttendance(bundle.concurrent_course_attendance);
      }

      // Update next lesson courses (clear when empty to avoid stale data)
      const nextCourses = (bundle.next_lesson_courses || [])
        .filter((c: any) => c.students?.length > 0);
      setNextLessonCourses(nextCourses.map((c: any) => ({
        course_name: c.course_name,
        room_name: c.room_name,
        teacher_names: c.teacher_names,
        is_cancelled: !!c.is_cancelled,
        students: c.students,
      })));
      // Reset consecutive lesson cancellation state
      setNextLessonIsCancelled(!!bundle.next_lesson_is_cancelled);
      setNextLessonSubject(bundle.next_lesson_subject || null);

      // Rebuild overview data from the fresh bundle
      const attendanceData = bundle.attendance || { present: [], late: [], absent: [] };
      const bundleStudentById = new Map(
        (bundle.students || []).map((s: any) => [String(s.id), s]),
      );
      const attendanceNameById = new Map<string, string>();
      for (const r of [
        ...(attendanceData.present || []),
        ...(attendanceData.late || []),
        ...(attendanceData.absent || []),
      ]) {
        if (r && typeof r === "object" && r.student_id && r.name) {
          attendanceNameById.set(String(r.student_id), String(r.name));
        }
      }
      const getUiStudentNameById = (sid: string): string => {
        const s = (refreshedStudents || []).find((stu: any) => stu.id === sid);
        if (s?.name) return s.name;
        const bs = bundleStudentById.get(sid);
        if (bs?.name) return String(bs.name);
        return attendanceNameById.get(sid) || "";
      };
      const getUiStudentClassById = (sid: string): string => {
        const s = (refreshedStudents || []).find((stu: any) => stu.id === sid);
        if (s?.className) return String(s.className);
        const bs = bundleStudentById.get(sid);
        return bs?.class_name ? String(bs.class_name) : "";
      };
      // Audit #23: surface Tagesgast students in the OVERVIEW too. The edit
      // path already tags `refreshedStudents[i].isGuest` (computed above from
      // fn_attendance_guest_student_ids), but the present/late/absent overview
      // rows didn't carry the flag — the read-only "Anwesenheit - Flexmodul"
      // panel showed guests as plain green pills with no visual cue. Build a
      // Set from refreshedStudents and tag each row.
      const isGuestById = new Set<string>(
        (refreshedStudents || [])
          .filter((s: any) => s?.isGuest)
          .map((s: any) => String(s.id)),
      );
      const getIsGuestById = (sid: string): boolean => isGuestById.has(String(sid));

      const late = attendanceData.late.map((r: any) => ({
        id: r.student_id,
        name: getUiStudentNameById(r.student_id),
        className: getUiStudentClassById(r.student_id),
        minutesLate: r.lateness_duration_minutes || r.late_minutes || 5,
        lateExcused: r.late_excused || false,
        isGuest: getIsGuestById(r.student_id),
      }));
      const absent = attendanceData.absent.map((r: any) => ({
        id: r.student_id,
        name: getUiStudentNameById(r.student_id),
        className: getUiStudentClassById(r.student_id),
        excused: r.status === "absent_excused",
        // absent_unclear epic: distinguish pre-klärung from formal
        // absent_unexcused — see paired producer above.
        unclear: r.status === "absent_unclear",
        reason: r.notes || "",
        isGuest: getIsGuestById(r.student_id),
      }));
      const explicitPresentIds = new Set<string>(
        attendanceData.present.map((r: any) =>
          typeof r === "string" ? r : r.student_id,
        ),
      );
      explicitPresentIdsRef.current = explicitPresentIds;
      const lateIds = new Set<string>(late.map((s: any) => s.id));
      const absentIds = new Set<string>(absent.map((s: any) => s.id));
      const inferredPresent: { id: string; name: string; className: string; isGuest: boolean }[] = [];
      (refreshedStudents || []).forEach((stu: any) => {
        const sid = String(stu.id);
        if (absentIds.has(sid) || lateIds.has(sid)) return;
        if (explicitPresentIds.has(sid)) return;
        inferredPresent.push({
          id: sid,
          name: getUiStudentNameById(sid),
          className: getUiStudentClassById(sid),
          isGuest: getIsGuestById(sid),
        });
      });
      const presentExplicit = attendanceData.present.map((r: any) => {
        const sid = typeof r === "string" ? r : r.student_id;
        const rpcName = typeof r === "object" ? r.name || "" : "";
        return {
          id: sid,
          name: getUiStudentNameById(sid) || rpcName,
          className: getUiStudentClassById(sid),
          notes: typeof r === "object" ? r.notes || "" : "",
          isGuest: getIsGuestById(sid),
        };
      });
      const present = [...presentExplicit];
      present.sort((a: any, b: any) => compareNames(a.name, b.name));
      inferredPresent.sort((a: any, b: any) => compareNames(a.name, b.name));
      late.sort((a: any, b: any) => compareNames(a.name, b.name));
      absent.sort((a: any, b: any) => compareNames(a.name, b.name));

      setOverviewData({
        lessonNote: bundle?.diary?.text || "",
        present,
        late,
        absent,
        pending: inferredPresent,
      });

      // Update badge counts
      if (bundle.badge_counts) {
        setBadgeCounts({
          present: present.length + inferredPresent.length,
          absent: absent.length,
          pending: inferredPresent.length,
        });
      }
    } catch (err) {
      console.error("[reloadBundle] error:", err);
    } finally {
      pauseRealtime(1000);
    }
  }, [lessonId, userProfile, triggerGetBundle, selectedLesson, setConcurrentCourseAttendance, explicitPresentIdsRef, fetchGuestStudentIds, pauseRealtime]);

  // ---------- Realtime: refresh concurrent course attendance on DB changes ----------
  // Subscribe to student_attendance_logs changes for CLASS lessons only.
  // Course lessons suppress the concurrent panel entirely.
  useEffect(() => {
    if (!open || !lessonId) return;
    // Skip for course lessons — they don't show concurrent data
    if (selectedLesson?.is_course) return;
    // Kiosk path supplies these as overrides; authenticated path uses userProfile.
    const schoolId = schoolIdOverride ?? (userProfile as any)?.school_id;
    const userId   = userIdOverride   ?? (userProfile as any)?.id;
    if (!schoolId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = subscribeToAttendanceEvents({
      schoolId,
      tables: ["student_attendance_logs"],
      onChange: () => {
        // Debounce: bulk saves fire many events at once
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          // Skip if an explicit reload is in progress
          if (suppressRealtimeRef.current) return;
          try {
            const result = await triggerGetBundle(
              {
                lessonId,
                schoolId,
                userId,
                flags: {
                  include_kb_overrides: false,
                  include_next_lesson: false,
                  include_recorder_meta: false,
                },
              },
              false,
            ).unwrap();
            if (result?.concurrent_course_attendance) {
              setConcurrentCourseAttendance(
                result.concurrent_course_attendance,
              );
            }
          } catch {
            /* silently ignore realtime refresh errors */
          }
        }, 500);
      },
    });

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lessonId, selectedLesson?.is_course]);

  // ---------- Return ----------
  return {
    selectedLesson,
    setSelectedLesson,
    attendanceViewMode,
    setAttendanceViewMode,
    overviewData,
    setOverviewData,
    attendanceLoading,
    lessonNote,
    setLessonNote,
    lessonHomework,
    setLessonHomework,
    initialLessonHomework,
    tempAttendance,
    setTempAttendance,
    explicitPresentIdsRef,
    editedStudentIds,
    setEditedStudentIds,
    protectionByStudent,
    setProtectionByStudent,
    overrideReasons,
    setOverrideReasons,
    lessonStatusOverrides,
    prefilledFromPrevious,
    prefilledAny,
    recordedMeta,
    setRecordedMeta,
    diaryMeta,
    setDiaryMeta,
    canEditExisting,
    setCanEditExisting,
    badgeCounts,
    setBadgeCounts,
    kbStudentIds,
    kbStudentTimes,
    earlierPresentIds,
    concurrentCourseAttendance,
    setConcurrentCourseAttendance,
    nextLessonCourses,
    reloadBundle,
    nextLessonIsCancelled,
    nextLessonSubject,
    keptInMainLesson,
    pendingTransfer,
    setPendingTransfer,
    transferReason,
    setTransferReason,
    transferredStudentIds,
    setTransferredStudentIds,
    frozenEditOrderRef,
    frozenEditLessonIdRef,
    suppressRealtimeRef,
    pauseRealtime,
    triggerGetBundle,
    transferStudent,
    isTransferring,
  };
}
